import { useId, useState } from 'react';
import { Dialog, DialogCancel } from './Dialog';
import { Button } from './Button';
import { Alert } from './Alert';
import { Textarea } from './Textarea';
import { isApiError, type ApiError } from '@/lib/api/errors';

/*
  Confirmation for a destructive action.

  What it insists on, because these are the parts that get dropped:

  * The consequence is a required prop and is rendered as prose above the buttons. "Are you
    sure?" is not a confirmation, it is a speed bump. The caller has to write down what will
    happen, and the device or record is named by the caller inside that copy.
  * A reason is collected when `requireReason` is set, because the API requires one for
    reboot and revoke and because the audit row is worthless without it. The confirm button
    stays disabled until the reason is long enough, so the round trip is not spent learning
    that.
  * Failure is rendered in the dialog, not swallowed and not left as a spinner. A 409 from a
    device policy and a 403 from a missing permission read differently and both say what to
    do next. The dialog stays open on failure so the reason the technician typed is not lost.
  * The dialog cannot be dismissed while the write is in flight, so there is no state where
    the request is running and the user believes they cancelled it.

  The form submits on Enter, because it is a real <form> with a submit button. A textarea
  takes Enter for a newline, which is why the reason field is a textarea and the submit
  happens from the button or from Enter in any single-line field a caller adds.
*/

export type ConfirmTone = 'danger' | 'warning';

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  /** One line under the title. Say which record this is about, by name. */
  readonly summary: React.ReactNode;
  /** What will happen. Required: this is the whole point of the dialog. */
  readonly consequence: React.ReactNode;
  readonly confirmLabel: string;
  readonly tone?: ConfirmTone;
  readonly requireReason?: boolean;
  readonly reasonLabel?: string;
  readonly reasonHint?: string;
  /** Extra controls between the consequence and the reason, for example a notify toggle. */
  readonly children?: React.ReactNode;
  /**
   * Resolve to close the dialog. Reject to keep it open and show the error. The reason is
   * always passed, and is the empty string when requireReason is false.
   */
  readonly onConfirm: (reason: string) => Promise<void>;
}

const MIN_REASON_LENGTH = 4;

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  summary,
  consequence,
  confirmLabel,
  tone = 'danger',
  requireReason = false,
  reasonLabel = 'Reason',
  reasonHint,
  children,
  onConfirm,
}: ConfirmDialogProps): React.JSX.Element {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [busy, setBusy] = useState(false);
  const formId = `confirm-${useId()}`;

  /*
    Reset when the dialog is reopened, so a previous failure or a previous reason does not
    reappear against a different target.

    This is React's documented "adjusting state during render" pattern rather than an effect,
    matching DevicesPage. An effect would render the stale reason for one frame before
    clearing it, which on a confirmation dialog means briefly showing the last justification
    next to a different device name. It is also what the set-state-in-effect lint rule is
    pointing at: the reset is derived from a prop change, not a synchronisation with anything
    outside React.
  */
  const [lastOpen, setLastOpen] = useState(open);
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) {
      setReason('');
      setError(null);
      setBusy(false);
    }
  }

  const reasonTooShort = requireReason && reason.trim().length < MIN_REASON_LENGTH;
  const fieldError = isApiError(error) ? error.fieldErrors.reason?.[0] : undefined;

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (busy || reasonTooShort) {
      return;
    }
    setBusy(true);
    setError(null);
    onConfirm(reason.trim())
      .then(() => {
        setBusy(false);
        onOpenChange(false);
      })
      .catch((cause: unknown) => {
        setBusy(false);
        setError(cause instanceof Error ? cause : new Error('The action failed.'));
      });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={summary}
      width="sm"
      busy={busy}
      footer={
        <>
          <DialogCancel disabled={busy} />
          <Button
            type="submit"
            form={formId}
            variant={tone === 'danger' ? 'danger' : 'primary'}
            loading={busy}
            disabled={reasonTooShort}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-3.5">
        <Alert
          tone={tone}
          title="What this does"
          // polite, not assertive: the dialog has just taken focus, so the screen reader is
          // already reading this region. An assertive alert here would interrupt itself.
          liveness="polite"
        >
          {consequence}
        </Alert>

        {children}

        {requireReason ? (
          <Textarea
            label={reasonLabel}
            hint={reasonHint ?? 'Recorded in the audit log against your name.'}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
            rows={3}
            required
            autoFocus
            maxLength={500}
            {...(fieldError === undefined ? {} : { error: fieldError })}
          />
        ) : null}

        {error !== null && fieldError === undefined ? (
          <Alert
            tone={isApiError(error) && error.kind === 'forbidden' ? 'warning' : 'danger'}
            title={confirmFailureTitle(error)}
            liveness="assertive"
          >
            {isApiError(error) ? (error.problem.detail ?? error.message) : error.message}
          </Alert>
        ) : null}
      </form>
    </Dialog>
  );
}

/**
 * Distinct titles per failure kind. "Something went wrong" tells a technician nothing; the
 * difference between "the server refused this" and "we could not reach the server" decides
 * whether they retry or go and ask for a permission.
 */
function confirmFailureTitle(error: Error): string {
  if (!isApiError(error)) {
    return 'The action failed';
  }
  switch (error.kind) {
    case 'network':
      return 'Could not reach the JIT server';
    case 'forbidden':
      return 'Refused: you do not have permission';
    case 'step_up_required':
      return 'A fresh second factor is required first';
    case 'conflict':
      return 'Refused: that conflicts with the current state';
    case 'validation':
      return 'The request was rejected';
    case 'not_found':
      return 'That record no longer exists';
    case 'rate_limited':
      return 'Too many requests. Wait and try again';
    case 'server':
      return 'The server failed to complete this';
    default:
      return 'The action failed';
  }
}
